/**
 * @fileoverview Number input component with increment/decrement buttons
 * @module ctl-number-input
 * @version 1.0.0
 * 
 * @description
 * A styled numeric input component with increment and decrement buttons (stepper).
 * Provides a user-friendly interface for entering numeric values with optional
 * min/max constraints and step increments. The component includes both keyboard
 * input and button controls for adjusting values.
 * 
 * The component automatically validates values against min/max bounds, supports
 * custom step increments, and can display labels and validation states. It's
 * ideal for quantity selectors, age inputs, or any numeric data entry.
 * 
 * @example
 * <!-- Basic number input -->
 * <ctl-number-input 
 *   label="Quantity" 
 *   value="1" 
 *   min="1" 
 *   max="99">
 * </ctl-number-input>
 * 
 * @example
 * <!-- Number input with decimal step -->
 * <ctl-number-input 
 *   label="Price" 
 *   value="19.99" 
 *   min="0" 
 *   step="0.01"
 *   placeholder="0.00">
 * </ctl-number-input>
 * 
 * @example
 * <!-- Disabled number input -->
 * <ctl-number-input 
 *   label="Age" 
 *   value="25" 
 *   disabled>
 * </ctl-number-input>
 * 
 * @example
 * <!-- Read-only number input -->
 * <ctl-number-input 
 *   label="Total" 
 *   value="150" 
 *   readonly>
 * </ctl-number-input>
 */

/**
 * Number Input Web Component
 * 
 * A styled numeric input with increment/decrement buttons and validation.
 * Enforces min/max bounds and supports custom step increments.
 * 
 * @class CtlNumberInput
 * @extends HTMLElement
 * 
 * @attr {string} label - Field label text
 * @attr {number} value - Current numeric value (default: 0)
 * @attr {number} min - Minimum allowed value
 * @attr {number} max - Maximum allowed value
 * @attr {number} step - Step increment/decrement amount (default: 1)
 * @attr {boolean} disabled - Whether input is disabled
 * @attr {boolean} readonly - Whether input is read-only
 * @attr {boolean} required - Whether input is required
 * @attr {string} tone - Visual tone: "neutral" | "danger" | "warning" | "success" (default: "neutral")
 * @attr {string} placeholder - Placeholder text
 * 
 * @fires ctl-input - Custom event dispatched on user typing; detail.value contains the current numeric value
 * @fires ctl-change - Custom event dispatched on committed value changes (e.g., increment/decrement buttons or blur); detail.value and detail.action (increment|decrement|input) are provided
 * 
 * @cssprop --font-family-base - Font family (default: 'Segoe UI', sans-serif)
 * @cssprop --spacing-xs - Extra small spacing (default: 8px)
 * @cssprop --spacing-sm - Small spacing (default: 12px)
 * @cssprop --font-size-sm - Small font size (default: 0.875rem)
 * @cssprop --font-size-md - Medium font size (default: 1rem)
 * @cssprop --font-weight-semibold - Semibold font weight (default: 600)
 * @cssprop --ctl-color-text - Text color (default: #000)
 * @cssprop --ctl-color-surface - Background color (default: #fff)
 * @cssprop --ctl-color-brand - Brand/focus color (default: #6200ee)
 * @cssprop --ctl-color-danger - Error color (default: #b00020)
 * @cssprop --border-color-default - Border color (default: #ccc)
 * @cssprop --border-radius-md - Border radius (default: 8px)
 * @cssprop --transition-normal - Transition duration (default: 0.2s ease)
 */
class CtlNumberInput extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._value = 0;
    
    // Bind event handlers to maintain proper 'this' context
    this._handleInput = this._handleInput.bind(this);
    this._handleChange = this._handleChange.bind(this);
    this._handleDecrement = this._handleDecrement.bind(this);
    this._handleIncrement = this._handleIncrement.bind(this);
    this._listenersAttached = false;
  }

  static get observedAttributes() {
    return ['label', 'value', 'min', 'max', 'step', 'disabled', 'readonly', 'required', 'tone', 'placeholder'];
  }

  // Track whether the deprecation warning has already been shown
  static _deprecationWarned = false;

  connectedCallback() {
    if (!CtlNumberInput._deprecationWarned) {
      CtlNumberInput._deprecationWarned = true;
      if (typeof console !== 'undefined' && console && typeof console.warn === 'function') {
        console.warn(
          '[ctl-number-input] Deprecated: use <ctl-text-field type="number"> instead. ' +
          'ctl-number-input will be removed in a future major version.'
        );
      }
    }
    this.render();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    const input = this.shadowRoot.querySelector('input');
    const decrementBtn = this.shadowRoot.querySelector('#decrement');
    const incrementBtn = this.shadowRoot.querySelector('#increment');

    if (input) {
      input.removeEventListener('input', this._handleInput);
      input.removeEventListener('change', this._handleChange);
    }

    if (decrementBtn) {
      decrementBtn.removeEventListener('click', this._handleDecrement);
    }

    if (incrementBtn) {
      incrementBtn.removeEventListener('click', this._handleIncrement);
    }
    this._listenersAttached = false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'value') {
        this._value = parseFloat(newValue) || 0;
      }
      this.render();
      this.attachEventListeners();
    }
  }

  get value() {
    return this._value;
  }

  set value(val) {
    const numVal = parseFloat(val) || 0;
    const min = parseFloat(this.getAttribute('min'));
    const max = parseFloat(this.getAttribute('max'));
    
    let finalVal = numVal;
    if (!isNaN(min) && numVal < min) finalVal = min;
    if (!isNaN(max) && numVal > max) finalVal = max;
    
    this._value = finalVal;
    this.setAttribute('value', finalVal);
  }

  increment() {
    const step = parseFloat(this.getAttribute('step')) || 1;
    this.value = this._value + step;
    this._dispatchChange('increment');
  }

  decrement() {
    const step = parseFloat(this.getAttribute('step')) || 1;
    this.value = this._value - step;
    this._dispatchChange('decrement');
  }

  _dispatchChange(action) {
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: {
        value: this._value,
        name: this.getAttribute('name') || null,
        action: action
      }
    }));
  }

  render() {
    this._listenersAttached = false;
    const label = this.getAttribute('label') || '';
    const placeholder = this.getAttribute('placeholder') || '';
    const value = this.getAttribute('value') || this._value || '';
    const min = this.getAttribute('min') || '';
    const max = this.getAttribute('max') || '';
    const step = this.getAttribute('step') || '1';
    const disabled = this.hasAttribute('disabled');
    const readonly = this.hasAttribute('readonly');
    const required = this.hasAttribute('required');
    const tone = this.getAttribute('tone') || 'neutral';
    const helpText = this.getAttribute('help-text') || '';
    const errorText = this.getAttribute('error-text') || '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--ctl-font-sans);
        }

        .field-wrapper {
          display: flex;
          flex-direction: column;
          gap: var(--ctl-space-2);
        }

        label {
          font-size: var(--ctl-font-size-base);
          font-weight: var(--ctl-font-weight-semibold);
          color: var(--ctl-color-text);
          display: flex;
          align-items: center;
          gap: var(--ctl-space-2);
        }

        .required {
          color: var(--ctl-color-danger);
        }

        .input-container {
          display: flex;
          align-items: stretch;
          border: 2px solid var(--ctl-color-border-2);
          border-radius: var(--ctl-radius-md);
          background: var(--ctl-color-surface);
          transition: border-color var(--ctl-transition-normal);
          overflow: hidden;
        }

        .input-container:focus-within {
          border-color: var(--ctl-color-brand);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--ctl-color-brand) 20%, transparent);
        }

        .input-container[data-tone="success"] {
          border-color: var(--ctl-color-success);
        }

        .input-container[data-tone="warning"] {
          border-color: var(--ctl-color-warning);
        }

        .input-container[data-tone="danger"] {
          border-color: var(--ctl-color-danger);
        }

        input[type="number"] {
          flex: 1;
          font-family: inherit;
          font-size: var(--ctl-font-size-lg);
          padding: var(--ctl-space-3);
          border: none;
          background: transparent;
          color: var(--ctl-color-text);
          text-align: center;
        }

        input[type="number"]:focus {
          outline: none;
        }

        input[type="number"]:disabled {
          color: var(--ctl-color-muted);
          cursor: not-allowed;
        }

        /* Hide number input spinners */
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }

        button {
          background: var(--ctl-color-surface);
          border: none;
          padding: 0 var(--ctl-space-4);
          cursor: pointer;
          color: var(--ctl-color-text);
          font-size: var(--ctl-font-size-xl);
          font-weight: var(--ctl-font-weight-bold);
          transition: background var(--ctl-transition-fast);
          min-width: 40px;
        }

        button:hover:not(:disabled) {
          background: var(--ctl-color-surface-hover);
        }

        button:active:not(:disabled) {
          background: var(--ctl-color-surface-2);
        }

        button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .help-text {
          font-size: var(--ctl-font-size-base);
          color: var(--ctl-color-muted);
        }

        .error-text {
          font-size: var(--ctl-font-size-base);
          color: var(--ctl-color-danger);
        }

        .input-container:has(input:disabled) {
          opacity: 0.6;
          cursor: not-allowed;
        }
      </style>

      <div class="field-wrapper">
        ${label ? `<label>
          ${label}
          ${required ? '<span class="required">*</span>' : ''}
        </label>` : ''}
        
        <div class="input-container" data-tone="${tone}">
          <button 
            id="decrement" 
            ${disabled || readonly ? 'disabled' : ''}
            aria-label="Decrease value"
          >−</button>
          <input
            type="number"
            value="${value}"
            placeholder="${placeholder}"
            ${min ? `min="${min}"` : ''}
            ${max ? `max="${max}"` : ''}
            step="${step}"
            ${disabled ? 'disabled' : ''}
            ${readonly ? 'readonly' : ''}
            ${required ? 'required' : ''}
          />
          <button 
            id="increment" 
            ${disabled || readonly ? 'disabled' : ''}
            aria-label="Increase value"
          >+</button>
        </div>

        ${helpText ? `<div class="help-text">${helpText}</div>` : ''}
        ${errorText ? `<div class="error-text">${errorText}</div>` : ''}
      </div>
    `;
  }

  _handleInput(e) {
    this._value = parseFloat(e.target.value) || 0;
    
    this.dispatchEvent(new CustomEvent('ctl-input', {
      bubbles: true,
      composed: true,
      detail: {
        value: this._value,
        name: this.getAttribute('name') || null,
        originalEvent: e
      }
    }));
  }

  _handleChange(e) {
    this.value = parseFloat(e.target.value) || 0;
    const input = this.shadowRoot.querySelector('input');
    if (input) {
      input.value = this._value; // Update display with validated value
    }
    this._dispatchChange('input');
  }

  _handleDecrement() {
    this.decrement();
  }

  _handleIncrement() {
    this.increment();
  }

  attachEventListeners() {
    if (this._listenersAttached) return;

    const input = this.shadowRoot.querySelector('input');
    const decrementBtn = this.shadowRoot.querySelector('#decrement');
    const incrementBtn = this.shadowRoot.querySelector('#increment');

    if (input) {
      input.addEventListener('input', this._handleInput);
      input.addEventListener('change', this._handleChange);
    }

    if (decrementBtn) {
      decrementBtn.addEventListener('click', this._handleDecrement);
    }

    if (incrementBtn) {
      incrementBtn.addEventListener('click', this._handleIncrement);
    }

    this._listenersAttached = true;
  }
}

customElements.define("ctl-number-input", CtlNumberInput);

export { CtlNumberInput };
